import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter } from "react-router-dom";
import { App } from "@/App";
import { useUnreadCount } from "@/hooks/useNotifications";
import { setUnreadBadge } from "@/lib/offline";
import "@/index.css";
// Proton (Carbon) design system, loaded after index.css so it wins over the
// Tailwind layers; legacy.css restyles the remaining old class names.
import "@/proton/fonts.css";
import "@/proton/tokens.css";
import "@/proton/base.css";
import "@/proton/components.css";
import "@/proton/mail.css";
import "@/proton/apps.css";
import "@/proton/legacy.css";

// ── Installed-app icon badge ──────────────────────────────────────────────
// Mirrors the unread notification count onto the OS app-icon badge in
// installed PWAs (feature-detected inside setUnreadBadge; a no-op elsewhere).
// Lives inside the QueryClientProvider so it can read the shared notifications
// query without spinning up its own poll.
function AppBadgeSync(): null {
  const unread = useUnreadCount();
  React.useEffect(() => { setUnreadBadge(unread); }, [unread]);
  return null;
}

// ── In-page error overlay ─────────────────────────────────────────────────
// Phone users can't open DevTools, so silent JS errors look like "buttons
// don't work". This catches *every* unhandled exception + promise rejection
// and renders a dismissable red banner at the top with the actual error,
// stack frame, and a Copy button. Without this we're flying blind on mobile.
//
// Installed BEFORE React renders so we even catch errors during initial
// bundle execution (e.g. lazy-import 404 after a deploy churn).
(function installGlobalErrorOverlay() {
  if (typeof window === "undefined") return;
  if ((window as Window & { __l7_err_installed?: boolean }).__l7_err_installed) return;
  (window as Window & { __l7_err_installed?: boolean }).__l7_err_installed = true;

  const seen = new Set<string>();
  function show(label: string, msg: string, stack?: string): void {
    const key = label + "|" + msg;
    if (seen.has(key)) return;          // dedupe identical errors
    seen.add(key);
    const container = ensureContainer();
    const card = document.createElement("div");
    card.style.cssText = "background:#7f1d1d;color:#fff;padding:10px 14px 14px;border-radius:8px;margin:6px;font:12px/1.4 ui-monospace,Consolas,monospace;box-shadow:0 4px 16px rgba(0,0,0,.5);max-width:100%;word-break:break-word;";
    const head = document.createElement("div");
    head.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:6px;";
    head.innerHTML = `<strong style="flex:1">⚠ ${escapeHtml(label)}</strong>`;
    const copyBtn = document.createElement("button");
    copyBtn.textContent = "copy";
    copyBtn.style.cssText = "background:#fff2;color:#fff;border:0;border-radius:4px;padding:2px 8px;font:11px ui-monospace,monospace;cursor:pointer;";
    copyBtn.onclick = () => {
      const txt = `${label}\n${msg}\n${stack ?? ""}`;
      navigator.clipboard?.writeText(txt).catch(() => {});
      copyBtn.textContent = "copied";
    };
    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕";
    closeBtn.style.cssText = "background:#fff2;color:#fff;border:0;border-radius:4px;padding:2px 8px;font:11px ui-monospace,monospace;cursor:pointer;";
    closeBtn.onclick = () => card.remove();
    head.appendChild(copyBtn);
    head.appendChild(closeBtn);
    const body = document.createElement("div");
    body.textContent = msg;
    body.style.cssText = "white-space:pre-wrap;";
    card.appendChild(head);
    card.appendChild(body);
    if (stack) {
      const stk = document.createElement("pre");
      stk.textContent = stack.split("\n").slice(0, 6).join("\n");
      stk.style.cssText = "margin:6px 0 0;opacity:.7;font-size:10px;white-space:pre-wrap;max-height:160px;overflow:auto;";
      card.appendChild(stk);
    }
    container.appendChild(card);
  }
  function ensureContainer(): HTMLDivElement {
    let c = document.getElementById("__l7_err_overlay") as HTMLDivElement | null;
    if (!c) {
      c = document.createElement("div");
      c.id = "__l7_err_overlay";
      c.style.cssText = "position:fixed;top:0;left:0;right:0;z-index:99999;pointer-events:auto;max-height:60vh;overflow-y:auto;";
      // Wait until <body> exists if installed before parse complete.
      if (document.body) document.body.appendChild(c);
      else document.addEventListener("DOMContentLoaded", () => document.body.appendChild(c!));
    }
    return c;
  }
  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
  }
  window.addEventListener("error", (e) => {
    show("JS error", e.message || "(no message)", e.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    const msg = r instanceof Error ? `${r.name}: ${r.message}` : typeof r === "string" ? r : JSON.stringify(r);
    show("Unhandled promise rejection", msg, r instanceof Error ? r.stack : undefined);
  });
})();

// One client for the whole app. Resources auto-refetch on window focus per
// JMAP / Mailtrap webhook arrival cadence; tune per-query as needed.
const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
        <AppBadgeSync />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
);

// ── PWA service worker registration ───────────────────────────────────────
// Registered after first paint so it never delays the initial render. The SW
// itself handles its own update lifecycle (skipWaiting + claim on activate),
// so a deploy is picked up automatically on the next page load.
//
// Disabled on http://localhost (development) — Vite's HMR doesn't play nicely
// with a SW intercepting navigation requests, and there's no PWA story for
// the dev server. Production builds always register.
if (
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  window.location.protocol === "https:"
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => console.warn("[sw] registration failed:", err));
  });
}
